import { useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  BookmarkIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import ButtonComponent from '../../customButton';
import SessionIdCopyButton from '../../common/SessionIdCopyButton';
import styles from '../styles/automationNew.module.scss';
import { RUN_LABEL } from '../constants';
import StatusPill from './StatusPill';

function formatUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '$0.00';
  if (v < 0.0001) return '<$0.0001';
  return `$${v.toFixed(4)}`;
}

function parseTokenUsage(tokenUsage) {
  if (tokenUsage == null) return { tokens: null, usd: null, tiers: [] };
  const input = Number(tokenUsage.input) || 0;
  const output = Number(tokenUsage.output) || 0;
  const tokens = input + output || Number(tokenUsage.total) || null;
  const usdN = Number(tokenUsage.usd);
  const tiers = Object.entries(tokenUsage.by_tier || {})
    .filter(([, d]) => d && (d.input || d.output))
    .map(([tier, d]) => ({
      tier,
      model: d.model || '—',
      input: Number(d.input) || 0,
      output: Number(d.output) || 0,
      total: (Number(d.input) || 0) + (Number(d.output) || 0),
      usd: Number(d.usd) || 0,
    }));
  return { tokens, usd: Number.isFinite(usdN) ? usdN : null, tiers };
}

function TokenUsageHover({ tokenUsage }) {
  const { tokens, usd, tiers } = parseTokenUsage(tokenUsage);
  if (tokens == null) return null;
  return (
    <div className={`${styles.metaItem} ${styles.metaItemDesktop} ${styles.tokenChip}`}>
      <span className={styles.tokenChipTrigger} tabIndex={0}>
        Tokens <span className={styles.metaStrong}>{tokens.toLocaleString()}</span>
      </span>
      <div className={styles.tokenHoverCard} role="tooltip">
        <div className={styles.tokenHoverTitle}>Token usage</div>
        {tiers.map((row) => (
          <div key={`${row.tier}-${row.model}`} className={styles.tokenHoverModel}>
            <div className={styles.tokenHoverModelHead}>
              <span className={styles.tokenHoverModelName}>{row.model}</span>
              {row.tier ? <span className={styles.tokenHoverTier}>{row.tier}</span> : null}
            </div>
            <dl className={styles.tokenHoverStats}>
              <div>
                <dt>Input</dt>
                <dd>{row.input.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Output</dt>
                <dd>{row.output.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{row.total.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Cost</dt>
                <dd>{formatUsd(row.usd)}</dd>
              </div>
            </dl>
          </div>
        ))}
        <div className={styles.tokenHoverTotal}>
          <span>Total</span>
          <span>
            {tokens.toLocaleString()}
            {usd != null && usd > 0 ? ` · ${formatUsd(usd)}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function StudioTopBar({
  runStatus,
  isRunning,
  isConnected,
  agentHealthy,
  connectionError,
  agentUrl,
  tokenUsage,
  lastResult,
  planId,
  onPlanIdChange,
  environments = [],
  environmentId,
  onEnvironmentChange,
  variablePayloadText,
  onVariablePayloadChange,
  canRun = true,
  preflightMessages = [],
  runBlocked = false,
  onRun,
  onReplay,
  onStop,
  onSavePlan,
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const label = runStatus?.label || RUN_LABEL.IDLE;
  const isLive = label === RUN_LABEL.RUNNING || label === RUN_LABEL.CONNECTING;
  const isConnecting = label === RUN_LABEL.CONNECTING;

  const healthClass =
    agentHealthy === true ? styles.healthOk : agentHealthy === false ? styles.healthBad : styles.healthUnknown;
  const healthLabel =
    agentHealthy === true ? 'healthy' : agentHealthy === false ? 'unreachable' : 'unknown';

  const handleCopyPlanId = async () => {
    if (!planId) return;
    try {
      await navigator.clipboard.writeText(planId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore clipboard errors */
    }
  };

  // why gate before agent: block Run when env/refs/agent fail preflight
  const runDisabled = Boolean(runBlocked);

  return (
    <>
      <header className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <span className={styles.brandMark} aria-hidden>
            A
          </span>
          <span className={styles.brandTitle}>Automation New</span>
          <span
            className={`${styles.connPill} ${styles.connPillDesktop} ${
              isConnected || isLive ? styles.connPillOn : ''
            }`}
          >
            {isConnecting ? 'Agent connecting' : isConnected || isLive ? 'Agent linked' : 'Agent idle'}
          </span>
          {isConnecting && (
            <CircularProgress size={14} thickness={5} color="primary" className={styles.connectingSpinner} />
          )}
          <span
            className={`${styles.healthDot} ${healthClass}`}
            role="status"
            aria-label={`Agent health: ${healthLabel}. URL ${agentUrl}`}
          />
        </div>

        <div className={styles.topBarCenter}>
          <StatusPill status={label.toLowerCase()} label={label} />
          <span className={`${styles.metaItem} ${styles.metaItemDesktop}`}>
            Step{' '}
            <span className={styles.metaStrong}>
              {runStatus?.currentStep || 0} / {runStatus?.totalSteps || 0}
            </span>
          </span>
          <span className={`${styles.metaItem} ${styles.metaItemDesktop}`}>
            <span className={styles.metaStrong}>{runStatus?.elapsed || '00:00:00'}</span> Elapsed
          </span>
          <TokenUsageHover tokenUsage={tokenUsage} />
          {planId && (
            <div className={`${styles.metaItem} ${styles.metaItemDesktop}`}>
              <SessionIdCopyButton sessionId={planId} label="Session ID" />
            </div>
          )}
        </div>

        <div className={styles.topBarRight}>
          {preflightMessages.map((msg) => (
            <span key={msg} className={styles.errorChip} title={msg}>
              {msg}
            </span>
          ))}
          {connectionError && !preflightMessages.includes(connectionError) && (
            <span className={styles.errorChip} title={connectionError}>
              {connectionError}
            </span>
          )}
          {lastResult?.summary && !connectionError && preflightMessages.length === 0 && (
            <span className={`${styles.resultChip} ${styles.metaItemDesktop}`} title={lastResult.summary}>
              {lastResult.test_status || (lastResult.success ? 'passed' : 'done')}
            </span>
          )}
          <select
            className={styles.envSelect}
            value={environmentId || ''}
            disabled={isRunning}
            onChange={(e) => onEnvironmentChange?.(e.target.value)}
            title="Environment"
            aria-label="Environment"
          >
            <option value="">Environment</option>
            {environments.map((env) => {
              const id = env.id || env.environment_id;
              const name = env.name || env.environment_name || id;
              return (
                <option key={id} value={id}>
                  {name}
                </option>
              );
            })}
          </select>

          {isRunning ? (
            <ButtonComponent
              ctaText="Stop"
              variant="outline"
              size="small"
              className="red600"
              startIcon={<StopIcon className="icon red600" style={{ width: 14, height: 14 }} />}
              onClick={onStop}
              tooltip="Stop run"
            />
          ) : (
            <>
              <ButtonComponent
                ctaText="Run"
                variant="primary"
                size="small"
                className="white bg-blue600"
                startIcon={<PlayIcon className="icon white" style={{ width: 14, height: 14 }} />}
                onClick={onRun}
                disabled={!canRun || runDisabled}
                tooltip={
                  runDisabled
                    ? preflightMessages[0] || 'Fix preflight issues'
                    : canRun
                      ? 'Run instructions'
                      : 'Add at least one instruction'
                }
              />
              <ButtonComponent
                ctaText="Replay"
                variant="outline"
                size="small"
                className="primary"
                startIcon={<PlayIcon className="icon" style={{ width: 14, height: 14 }} />}
                onClick={onReplay}
                disabled={!planId || runDisabled}
                tooltip={
                  runDisabled
                    ? preflightMessages[0] || 'Fix preflight issues'
                    : planId
                      ? 'Replay saved plan'
                      : 'Run a record first to unlock Replay'
                }
              />
            </>
          )}
          <ButtonComponent
            ctaText="Save"
            variant="outline"
            size="small"
            className="primary"
            startIcon={<BookmarkIcon className="icon" style={{ width: 14, height: 14 }} />}
            onClick={() => onSavePlan?.()}
            disabled={!planId || isRunning}
            tooltip={planId ? 'Save plan' : 'Complete a run first to save a plan'}
          />
          <ButtonComponent
            type="iconbutton"
            variant="outline"
            size="small"
            startIcon={<Cog6ToothIcon className="icon" style={{ width: 16, height: 16 }} />}
            onClick={() => setShowSettings((v) => !v)}
            tooltip="Settings"
          />
        </div>
      </header>

      {showSettings && (
        <div className={styles.settingsBar}>
          <label className={styles.settingsField}>
            <span>Agent</span>
            <input className={styles.fieldInput} value={agentUrl} readOnly />
          </label>
          <label className={styles.settingsField}>
            <span>Plan ID (for Replay)</span>
            <div className={styles.planIdRow}>
              <input
                className={styles.fieldInput}
                value={planId || ''}
                disabled={isRunning}
                onChange={(e) => onPlanIdChange?.(e.target.value)}
                placeholder="Filled after a successful record"
              />
              <ButtonComponent
                type="iconbutton"
                variant="outline"
                size="small"
                startIcon={<ClipboardDocumentIcon className="icon" style={{ width: 14, height: 14 }} />}
                onClick={handleCopyPlanId}
                disabled={!planId}
                tooltip={copied ? 'Copied' : 'Copy plan ID'}
              />
            </div>
          </label>
          <label className={styles.settingsFieldWide}>
            {/* security: secrets — Settings cannot smuggle secret values to the agent */}
            <span>Variables metadata JSON (optional; values ignored)</span>
            <textarea
              className={styles.fieldTextarea}
              rows={2}
              value={variablePayloadText}
              disabled={isRunning}
              onChange={(e) => onVariablePayloadChange?.(e.target.value)}
              placeholder='{"variableMetadata":{"uuid":{"key":"KEY","isSecret":true}}}'
            />
          </label>
        </div>
      )}
    </>
  );
}
